import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Stack,
  Pagination,
  Typography,
} from "@mui/material";

const GenericTable = ({
  columns = [],
  data = [],
  isLoading = false,
  page = 1,
  limit = 10,
  totalPages = 1,
  onPageChange,
  height = 420,
  headerBgColor = "#6e3fc9",
  emptyMessage = "No records found.",
  sx = {},
}) => {
  return (
    <Box sx={{ height, display: "flex", flexDirection: "column", ...sx }}>
      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          overflow: isLoading ? "hidden" : "auto",
          borderRadius: "16px 16px 0px 0",
          boxShadow: "none",
          border: "1px solid #e0e0e0",
          scrollBehavior: "smooth",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align || "left"}
                  sx={{
                    backgroundColor: headerBgColor,
                    color: "white",
                    fontWeight: "600", // Slightly lighter than bold for better readability
                    fontSize: "0.875rem",
                    whiteSpace: "nowrap", // Prevents headers from breaking into two lines
                    ...col.headerSx,
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              [...Array(limit)].map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((col) => (
                    <TableCell key={col.id}>
                      <Skeleton
                        variant="text"
                        width={col.id === "sno" ? "40%" : "85%"}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data && data.length > 0 ? (
              data.map((row, index) => (
                <TableRow
                  key={row._id || row.id || index}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": { backgroundColor: "#f8f5ff" },
                    transition: "background-color 0.2s ease",
                  }}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={col.id}
                      align={col.align || "left"}
                      sx={{ fontSize: "0.875rem", color: "#333" }}
                    >
                      {col.render
                        ? col.render(row, index)
                        : row[col.id] ?? "N/A"}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ py: 10 }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="body1"
                      sx={{ color: "#999", fontWeight: 500 }}
                    >
                      {emptyMessage}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack
        spacing={2}
        sx={{
          width: "100%",
          alignItems: "flex-end",
          mt: 2,
          pb: 1,
        }}
      >
        <Pagination
          count={totalPages}
          page={page}
          disabled={totalPages <= 1 && data.length === 0}
          onChange={(e, val) => onPageChange(val)}
          sx={{
            "& .MuiPaginationItem-root": {
              color: headerBgColor,
              borderColor: headerBgColor,
              "&:hover": { backgroundColor: "#f2ecfc" },
              "&.Mui-selected": {
                backgroundColor: `${headerBgColor} !important`,
                color: "#fff !important",
                fontWeight: "bold",
              },
            },
          }}
        />
      </Stack>
    </Box>
  );
};

export default GenericTable;
